/**
 * SİPARİŞ DURUMU — sıra, etiket, rozet ve "Açık siparişler" kısayolu için TEK KAYNAK.
 *
 * Neden: durum etiketleri ekranlar arasında kopya kopyaydı ve ayrışmıştı ("Hazır" ↔ "Sevkiyata
 * Hazır"). Kullanıcı aynı siparişi Siparişler ekranında bir adla, MRP seçicisinde ya da A4
 * Teslimat Planı'nda başka bir adla görüyordu. Siparişler ekranı, MRP seçicisi, Teslimat Planı ve
 * MRP Raporu buradan okur; yeni bir ekran durum gösterecekse kendi sözlüğünü YAZMAZ.
 *
 * Süzgeç girdisi de buradan kurulur (`durumSuzgecGirdisi`, `turSuzgecGirdisi`) ki üç ekran
 * sunucuya AYNI şekli göndersin (sunucu tarafı: routers/order.ts `siparisSuzgecSekli`).
 */
import type { SSOption } from "../components/ui/SearchSelect";

export type SiparisDurumu = "draft" | "confirmed" | "in_production" | "ready" | "shipped" | "cancelled";

/** Yaşam döngüsü sırası — seçenek listeleri ve seçim özetleri bu sırayla dizilir. */
export const SIPARIS_DURUM_SIRASI: readonly SiparisDurumu[] = [
  "draft", "confirmed", "in_production", "ready", "shipped", "cancelled",
];

/** Etiketler — "ready" için Siparişler ekranının yerleşik adı ("Hazır") esas alındı (14 Eki 2026). */
export const SIPARIS_DURUM_ETIKET: Record<SiparisDurumu, string> = {
  draft: "Taslak",
  confirmed: "Onaylı",
  in_production: "Üretimde",
  ready: "Hazır",
  shipped: "Sevk Edildi",
  cancelled: "İptal",
};

/** Masaüstü `.badge-pill` ton sınıfı (blocks.css). Taslak nötr (sınıfsız). */
const ROZET: Record<SiparisDurumu, string> = {
  draft: "",
  confirmed: "b-success",
  in_production: "b-warn",
  ready: "b-success",
  shipped: "b-info",
  cancelled: "b-danger",
};

export function siparisDurumuMu(s: string): s is SiparisDurumu {
  return (SIPARIS_DURUM_SIRASI as readonly string[]).includes(s);
}

/** Bilinmeyen bir durum kodu gelirse kodun KENDİSİ gösterilir — sessizce boş basılmaz. */
export function durumEtiketi(s: string): string {
  return siparisDurumuMu(s) ? SIPARIS_DURUM_ETIKET[s] : s;
}

/** Masaüstü rozet sınıfı (`badge-pill ${…}`). */
export function durumRozetSinifi(s: string): string {
  return siparisDurumuMu(s) ? ROZET[s] : "";
}

/** Mobil `.m-app .badge` API'si farklı adlar kullanır (b-warn → b-warning, nötr → b-neutral). */
export function durumRozetSinifiMobil(s: string): string {
  const c = durumRozetSinifi(s);
  return c === "b-warn" ? "b-warning" : c || "b-neutral";
}

/** Durum süzgeci seçenekleri — SearchSelect ve mobil çipler AYNI listeyi kullanır. */
export const DURUM_SECENEKLERI: SSOption[] = SIPARIS_DURUM_SIRASI.map((s) => ({ value: s, label: SIPARIS_DURUM_ETIKET[s] }));

/** Seçimi yaşam döngüsü sırasına dizer + tekrarları atar → sorgu anahtarı seçim sırasından bağımsız olur. */
export function durumlariSirala(degerler: readonly string[]): SiparisDurumu[] {
  const set = new Set(degerler);
  return SIPARIS_DURUM_SIRASI.filter((s) => set.has(s));
}

// ─── "AÇIK SİPARİŞLER" KISAYOLU ────────────────────────────────────────────────────────────
/**
 * Müşterinin "toplu göremiyoruz" dediği üç durum (Maya Food, 14 Eki 2026). Varsayılan seçim
 * DEĞİL — varsayılan "Tümü" kalır; bu küme tek tıkla uygulanan bir kısayoldur.
 */
export const ACIK_SIPARISLER: readonly SiparisDurumu[] = ["draft", "confirmed", "in_production"];
export const ACIK_SIPARISLER_AD = "Açık siparişler";
/** Kümenin İÇERİĞİ — kısayol etiketi neyi seçtiğini söylesin. */
export const ACIK_SIPARISLER_KUME = ACIK_SIPARISLER.map((s) => SIPARIS_DURUM_ETIKET[s]).join(" · ");
/** Tam etiket: "Açık siparişler (Taslak · Onaylı · Üretimde)". */
export const ACIK_SIPARISLER_ETIKET = `${ACIK_SIPARISLER_AD} (${ACIK_SIPARISLER_KUME})`;

/** Seçim kısayol kümesine BİREBİR eşit mi (sıra önemsiz)? */
export function acikSiparislerSecili(secili: readonly string[]): boolean {
  const set = new Set(secili);
  return set.size === ACIK_SIPARISLER.length && ACIK_SIPARISLER.every((s) => set.has(s));
}

/**
 * Kısayola basış: seçim zaten kısayolsa "Tümü"ne döner (aç/kapa), değilse kısayolu uygular.
 * İki ekran (Siparişler, MRP seçicisi) aynı davranışı bu fonksiyondan alır.
 */
export function acikSiparislerKisayolu(secili: readonly string[]): SiparisDurumu[] {
  return acikSiparislerSecili(secili) ? [] : [...ACIK_SIPARISLER];
}

// ─── GENEL MRP TALEP KÜMESİ ────────────────────────────────────────────────────────────────
/**
 * "MRP Çalıştır" (genel MRP) talebine giren durumlar — `ACIK_SIPARISLER` ile AYNI KÜME DEĞİL:
 * taslak girmez, hazır girer. Bu yüzden ekranda genel MRP'nin kapsamı "açık siparişler" diye
 * ANLATILMAZ, durum adlarıyla yazılır (`GENEL_MRP_KUME`); aksi hâlde aynı kartta "açık sipariş"
 * iki farklı kümeyi anlatır ve kullanıcı kısayolla genel MRP'nin kapsamını seçtiğini sanır.
 *
 * ⚠ AYNA: sunucu kaynağı `apps/api/src/mrp.ts` `MRP_TALEP_DURUMLARI` (istemci sunucu kodunu
 * paketleyemediği için kopya). Parite `apps/api/scripts/test-order-status-type-filter.ts` bölüm L.
 */
export const GENEL_MRP_DURUMLARI: readonly SiparisDurumu[] = ["confirmed", "in_production", "ready"];
/** "Onaylı · Üretimde · Hazır". */
export const GENEL_MRP_KUME = GENEL_MRP_DURUMLARI.map((s) => SIPARIS_DURUM_ETIKET[s]).join(" · ");

/**
 * Seçim özeti (tetikleyici metni / künye): boş → "Tüm durumlar"; kısayola eşit → kısayol adı;
 * aksi hâlde kısa liste (en çok iki ad + kalan sayısı).
 */
export function durumSecimOzeti(secili: readonly string[], opts: { tam?: boolean } = {}): string {
  const sirali = durumlariSirala(secili);
  if (sirali.length === 0) return "Tüm durumlar";
  if (acikSiparislerSecili(sirali)) return opts.tam ? ACIK_SIPARISLER_ETIKET : ACIK_SIPARISLER_AD;
  const adlar = sirali.map((s) => SIPARIS_DURUM_ETIKET[s]);
  if (opts.tam || adlar.length <= 2) return adlar.join(" · ");
  return `${adlar.slice(0, 2).join(" · ")} +${adlar.length - 2}`;
}

/** Sunucu girdisi: boş seçim = süzgeç YOK (alan hiç gönderilmez → "Tümü"). */
export function durumSuzgecGirdisi(secili: readonly string[]): { statuses?: SiparisDurumu[] } {
  const sirali = durumlariSirala(secili);
  return sirali.length ? { statuses: sirali } : {};
}

/**
 * URL OKUMA KURALI — durum ve tür okuyucularının ORTAK kuralı: URL'deki hiçbir değer SESSİZCE
 * kaybolmaz. Bozuk/eski bir yer imi raporu süzgeçsiz açıp kâğıda "tüm siparişler"i süzülmüş gibi
 * basamaz — değer ya süzgece girer ya da tanınmadığı ekranda VE künyede adıyla söylenir.
 *  · Durum: küme istemcide KAPALI ve bilinir → tanınmayan değer süzgece girmez, `taninmayan`da
 *    döner; çağıran onu göstermek ZORUNDA (Teslimat Planı: not + künye).
 *  · Tür: kimlik ancak sunucuda doğrulanabilir (pasif bir tür de geçerli süzgeçtir) → değer AYNEN
 *    iletilir; listede olmayan kimlik `turSecimOzeti`nde "N tür (listede yok)" diye sayılır, biçimce
 *    bozuk kimliği sunucu şeması reddeder ("Rapor yüklenemedi" — yine görünür).
 */
export interface UrlDurumSecimi {
  durumlar: SiparisDurumu[];
  /** URL'de olup tanınmayan değerler (ham hâliyle, tekrarsız) — GÖSTERİLMELİ. */
  taninmayan: string[];
}

/**
 * URL'den durum okuma — YENİ biçim `statuses=a,b` + GERİYE UYUM `status=a` (eski yer imleri,
 * Siparişler ekranının eski sürümünden açılmış sekmeler). İkisi birlikte gelirse BİRLEŞİM
 * (sunucudaki `siparisDurumKumesi` ile aynı kural). Tanınmayan değer ATILMAZ, ayrıca döner.
 */
export function durumlariUrldenOku(sp: URLSearchParams): UrlDurumSecimi {
  const ham = [...(sp.get("statuses") ?? "").split(","), sp.get("status") ?? ""].map((s) => s.trim()).filter(Boolean);
  return {
    durumlar: durumlariSirala(ham),
    taninmayan: [...new Set(ham.filter((s) => !siparisDurumuMu(s)))],
  };
}

/** Tanınmayan URL değerleri → okunur liste: “bogus”, “eski”. */
const tirnakla = (xs: readonly string[]) => xs.map((x) => `“${x}”`).join(", ");

/**
 * Ekran notu: URL'deki tanınmayan durum(lar) — yoksa null. Kâğıda da basılır (Teslimat Planı
 * `.dpp-note` baskıda gizlenmez): rapor hangi değeri YOK SAYDIĞINI söylemeden basılmaz.
 */
export function taninmayanDurumNotu(taninmayan: readonly string[]): string | null {
  if (taninmayan.length === 0) return null;
  return `Bağlantıdaki ${taninmayan.length} durum değeri tanınmadı ve süzgece alınmadı: ${tirnakla(taninmayan)}.`;
}

/**
 * Künye "Durum" parçası. Süzgeç yoksa null (eskisi gibi künyede durum yazmaz) — AMA URL'de
 * tanınmayan değer varsa süzgeç yok sayılmış demektir ve bu "Tüm durumlar" diye AÇIKÇA basılır;
 * aksi hâlde kâğıt bağlantının istediği süzgeç uygulanmış gibi okunurdu.
 */
export function durumKunyesi(durumlar: readonly string[], taninmayan: readonly string[] = []): string | null {
  const sirali = durumlariSirala(durumlar);
  if (sirali.length === 0 && taninmayan.length === 0) return null;
  const ek = taninmayan.length ? ` (bağlantıdaki ${tirnakla(taninmayan)} tanınmadı, yok sayıldı)` : "";
  return `Durum: ${durumSecimOzeti(sirali, { tam: true })}${ek}`;
}

// ─── ÜRÜN TÜRÜ (KART TÜRÜ) SÜZGECİ ─────────────────────────────────────────────────────────
/**
 * "Tür atanmamış" seçeneğinin süzgeç içindeki değeri. Gerçek tür kimlikleri cuid olduğu için
 * çakışmaz. Sunucuya `turAtanmamis: true` olarak gider — kimlik listesine KARIŞTIRILMAZ.
 */
export const TUR_ATANMAMIS = "__tur_atanmamis__";
export const TUR_ATANMAMIS_ETIKET = "Tür atanmamış";

/** Seçimi sunucu girdisine çevirir: kimlikler `itemTypeIds`, "Tür atanmamış" `turAtanmamis`. */
export function turSuzgecGirdisi(secili: readonly string[]): { itemTypeIds?: string[]; turAtanmamis?: true } {
  const ids = [...new Set(secili.filter((v) => v !== TUR_ATANMAMIS))].sort();
  return {
    ...(ids.length ? { itemTypeIds: ids } : {}),
    ...(secili.includes(TUR_ATANMAMIS) ? { turAtanmamis: true as const } : {}),
  };
}

/**
 * URL'den tür seçimi: `itemTypeIds=a,b` + `turAtanmamis=1`. Değerler AYNEN iletilir — bkz. yukarıdaki
 * URL OKUMA KURALI (tanınmayan kimlik süzgeçten düşmez; özette sayılır, bozuğu sunucu reddeder).
 */
export function turleriUrldenOku(sp: URLSearchParams): string[] {
  const ids = (sp.get("itemTypeIds") ?? "").split(",").map((s) => s.trim()).filter(Boolean);
  return sp.get("turAtanmamis") === "1" ? [...ids, TUR_ATANMAMIS] : ids;
}

/** Seçimi URL'ye yazar (Teslimat Planı'na devir). Boş seçim parametre üretmez. */
export function suzgeciUrlyeYaz(p: URLSearchParams, durumlar: readonly string[], turler: readonly string[]): void {
  const d = durumSuzgecGirdisi(durumlar);
  if (d.statuses) p.set("statuses", d.statuses.join(","));
  const t = turSuzgecGirdisi(turler);
  if (t.itemTypeIds) p.set("itemTypeIds", t.itemTypeIds.join(","));
  if (t.turAtanmamis) p.set("turAtanmamis", "1");
}

/** Kart türü listesi (settings.itemType.list) → süzgeç seçenekleri: ad + kod (mono kuyruk) + "Tür atanmamış". */
export function turSecenekleri(turler: ReadonlyArray<{ id: string; code: string; name: string }>): SSOption[] {
  return [
    ...turler.map((t) => ({ value: t.id, label: t.name, tail: t.code })),
    { value: TUR_ATANMAMIS, label: TUR_ATANMAMIS_ETIKET, desc: "Türü boş mamul kartları" },
  ];
}

/** Tür seçim özeti: en çok iki ad, fazlası sayı. Listede bulunamayan kimlik SESSİZCE düşmez, sayılır. */
export function turSecimOzeti(secili: readonly string[], turler: ReadonlyArray<{ id: string; name: string }>, opts: { tam?: boolean } = {}): string {
  if (secili.length === 0) return "Tüm ürün türleri";
  const adlar: string[] = [];
  let bilinmeyen = 0;
  for (const v of secili) {
    if (v === TUR_ATANMAMIS) { adlar.push(TUR_ATANMAMIS_ETIKET); continue; }
    const t = turler.find((x) => x.id === v);
    if (t) adlar.push(t.name); else bilinmeyen++;
  }
  const ek = bilinmeyen ? [`${bilinmeyen} tür (listede yok)`] : [];
  if (opts.tam || adlar.length + ek.length <= 2) return [...adlar, ...ek].join(" · ");
  const gosterilen = adlar.slice(0, 2);
  return `${gosterilen.join(" · ")} +${adlar.length + bilinmeyen - gosterilen.length}`;
}
